import React, { useState, useEffect } from 'react';
import {
  Plus, Warehouse, Download, Eye, List, FileText,
  FileSpreadsheet, X, Save, Building2, Settings
} from 'lucide-react';
import WarehouseCanvas from './WarehouseCanvas';
import ListWarehouse from './ListWarehouse';
import { authFetch } from "../utils/authFetch";

// ============================================
// CONFIGURACIÓN DE COLORES
// ============================================
const COLORS = {
  // Colores principales
  primary: '#2563eb',           // Azul principal
  primaryHover: '#1d4ed8',      // Azul hover
  primaryLight: '#3b82f6',      // Azul claro
  
  // Colores secundarios
  secondary: '#64748b',         // Gris azulado
  secondaryHover: '#475569',    // Gris azulado oscuro
  
  // Colores de acento
  accent: '#2563eb',           // Cyan/Azul cielo
  accentHover: '#1d4ed8',      // Cyan oscuro
  
  // Colores de éxito
  success: '#10b981',          // Verde
  successHover: '#059669',     // Verde oscuro
  
  // Colores neutros
  neutral: '#f8fafc',          // Fondo claro
  neutralBorder: '#e2e8f0',    // Bordes
  neutralText: '#64748b',      // Texto secundario
  
  // Colores de estado
  danger: '#ef4444',
  warning: '#f59e0b',
};

const WarehouseManagement = ({ token }) => {
  const API_URL = process.env.REACT_APP_API_URL
    || (window.location.hostname === 'localhost'
      ? 'http://localhost:3001/api'
      : 'https://alvarezbohlback-production.up.railway.app/api');

  // Estados principales
  const [almacenes, setAlmacenes] = useState([]);
  const [almacenSeleccionado, setAlmacenSeleccionado] = useState('');
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [snapToGrid, setSnapToGrid] = useState(true);
  
  // Cargar vista por defecto desde localStorage
  const getDefaultView = () => {
    const saved = localStorage.getItem('warehouse_default_view');
    return saved || 'visual'; // Por defecto: visual
  };
  
  const [viewMode, setViewMode] = useState(getDefaultView());

  // Estados para vista visual
  const [zonas, setZonas] = useState([]);
  const [maxZIndex, setMaxZIndex] = useState(100);

  // Estados para vista lista
  const [estructuraCompleta, setEstructuraCompleta] = useState(null);
  const [filtros, setFiltros] = useState({
    search: '',
    zona: '',
    linea: '',
    columna: '',
    nivel: '',
    expiry: 'all',
    codigo_ubicacion: ''
  });

  // Estados de modales
  const [showCreateWarehouseModal, setShowCreateWarehouseModal] = useState(false);
  const [showCreateZoneModal, setShowCreateZoneModal] = useState(false);
  const [showCreateLineaModal, setShowCreateLineaModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Estados de formularios
  const [newWarehouse, setNewWarehouse] = useState({
    codigo: '',
    nombre: '',
    descripcion: ''
  });

  const [newZone, setNewZone] = useState({
    nombre: '', 
    codigo: '', 
    color: COLORS.primary,
    x: 50, 
    y: 50, 
    width: 300, 
    height: 500
  });

  const [newLinea, setNewLinea] = useState({
    zona_id: null,
    numero: 1,
    total_columnas: 12
  });

  // Estado para feedback de creación
  const [creatingZone, setCreatingZone] = useState(false);

  // Valores por defecto para columnas
  const DEFAULT_COLUMNA_CONFIG = {
    columna_width: 45,
    columna_height: 45,
    columna_spacing: 3,
    total_niveles: 7,
    nivel_picking_inicio: 1,
    nivel_picking_fin: 2
  };

  // Effects
  useEffect(() => {
    if (token) {
      fetchAlmacenes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (almacenSeleccionado && token) {
      if (viewMode === 'visual') {
        fetchAlmacenData(almacenSeleccionado);
      } else {
        fetchEstructuraCompleta(almacenSeleccionado);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [almacenSeleccionado, viewMode, token]);

  useEffect(() => {
    if (viewMode === 'list' && almacenSeleccionado && token) {
      fetchEstructuraCompleta(almacenSeleccionado);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros, viewMode, almacenSeleccionado, token]);

  // Guardar vista por defecto cuando cambia
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem('warehouse_default_view', mode);
  };

  // ============================================
  // API CALLS
  // ============================================

  const fetchAlmacenes = async () => {
    try {
      const response = await authFetch(`${API_URL}/warehouse/almacenes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        setAlmacenes(result.data);
        if (result.data.length > 0 && !almacenSeleccionado) {
          setAlmacenSeleccionado(result.data[0].codigo);
        }
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchAlmacenData = async (codigo) => {
    setLoading(true);
    try {
      const response = await authFetch(`${API_URL}/warehouse/almacenes/${codigo}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        const zonasOrdenadas = result.data.zonas?.map((zona, index) => ({
          ...zona,
          zIndex: zona.zIndex || (100 + index), // Usar zIndex del backend o asignar por orden
          lineas: zona.lineas?.sort((a, b) => a.numero - b.numero).map(linea => {
            const columnasCount = linea.columnas?.length || 0;
            const columnaWidth = DEFAULT_COLUMNA_CONFIG.columna_width;
            const columnaHeight = DEFAULT_COLUMNA_CONFIG.columna_height;
            const columnaSpacing = DEFAULT_COLUMNA_CONFIG.columna_spacing;

            // Single vertical column: all columnas stacked top to bottom
            const calculatedWidth = columnaWidth + (columnaSpacing * 2);
            const calculatedHeight = (columnaHeight * columnasCount) + (columnaSpacing * (columnasCount + 1)) + 30;

            return {
              ...linea,
              width: calculatedWidth,
              height: calculatedHeight,
              columnas: linea.columnas?.sort((a, b) => a.numero - b.numero).map((columna, idx) => ({
                ...columna,
                width: columnaWidth,
                height: columnaHeight,
                x: columnaSpacing,
                y: 30 + columnaSpacing + (idx * (columnaHeight + columnaSpacing))
              }))
            };
          })
        }));
        
        setZonas(zonasOrdenadas || []);
        
        // Calcular maxZIndex basado en las zonas existentes
        if (zonasOrdenadas && zonasOrdenadas.length > 0) {
          const maxZ = Math.max(...zonasOrdenadas.map(z => z.zIndex || 100));
          setMaxZIndex(maxZ);
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEstructuraCompleta = async (codigo) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.keys(filtros).forEach(key => {
        if (filtros[key] && filtros[key] !== '' && filtros[key] !== 'all') {
          params.append(key, filtros[key]);
        }
      });

      const url = `${API_URL}/warehouse/almacenes/${codigo}/estructura-completa${params.toString() ? '?' + params.toString() : ''}`;
      
      const response = await authFetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        setEstructuraCompleta(result.data);
      }
    } catch (error) {
      console.error('Error fetching estructura completa:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format) => {
    setExportLoading(true);
    try {
      const response = await authFetch(`${API_URL}/warehouse/almacenes/${almacenSeleccionado}/export/${format}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Error al generar el informe');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      const timestamp = new Date().toISOString().split('T')[0];
      let filename = `inventario_${almacenSeleccionado}_${timestamp}`;
      
      switch (format) {
        case 'csv': filename += '.csv'; break;
        case 'excel': filename += '.xlsx'; break;
        case 'pdf': filename += '.pdf'; break;
        default: break;
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setShowExportModal(false);
    } catch (error) {
      console.error('Error exporting report:', error);
      alert('Error al generar el informe');
    } finally {
      setExportLoading(false);
    }
  };

  const createWarehouse = async () => {
    try {
      if (!newWarehouse.codigo || !newWarehouse.nombre) {
        alert('Por favor, completa el código y nombre del almacén');
        return;
      }

      const response = await authFetch(`${API_URL}/warehouse/almacenes`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          codigo: newWarehouse.codigo.toUpperCase(),
          nombre: newWarehouse.nombre,
          descripcion: newWarehouse.descripcion
        })
      });
      if (!response) return;

      const result = await response.json();
      
      if (result.success) {
        await fetchAlmacenes();
        setAlmacenSeleccionado(newWarehouse.codigo.toUpperCase());
        setShowCreateWarehouseModal(false);
        setNewWarehouse({ codigo: '', nombre: '', descripcion: '' });
        alert('✅ Almacén creado exitosamente');
      } else {
        alert(`❌ Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al crear el almacén');
    }
  };

  const createZona = async () => {
    try {
      if (!newZone.codigo || !newZone.nombre) {
        alert('Por favor, completa el código y nombre de la zona');
        return;
      }

      setCreatingZone(true);

      // Asignar el siguiente zIndex disponible (siempre al frente)
      const newZIndex = maxZIndex + 1;
      
      const response = await authFetch(`${API_URL}/warehouse/almacenes/${almacenSeleccionado}/zonas`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          nombre: newZone.nombre,
          codigo_zona: newZone.codigo,
          x: newZone.x,
          y: newZone.y,
          width: newZone.width,
          height: newZone.height,
          color: newZone.color,
          zIndex: newZIndex // Enviar zIndex al backend
        })
      });
      if (!response) return;

      const result = await response.json();
      
      if (result.success) {
        setMaxZIndex(newZIndex);
        await fetchAlmacenData(almacenSeleccionado);
        setShowCreateZoneModal(false);
        setNewZone({
          nombre: '', 
          codigo: '', 
          color: COLORS.primary,
          x: 50, 
          y: 50, 
          width: 300, 
          height: 500
        });
        
        // Mostrar notificación de éxito
        const notification = document.createElement('div');
        notification.style.cssText = `
          position: fixed;
          top: 80px;
          right: 24px;
          background: #10b981;
          color: white;
          padding: 16px 24px;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          z-index: 10000;
          font-weight: 600;
          animation: slideIn 0.3s ease;
        `;
        notification.textContent = `✅ Zona "${result.data.codigo}" creada exitosamente`;
        document.body.appendChild(notification);
        
        setTimeout(() => {
          notification.style.animation = 'slideOut 0.3s ease';
          setTimeout(() => document.body.removeChild(notification), 300);
        }, 3000);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al crear la zona');
    } finally {
      setCreatingZone(false);
    }
  };

  const createLinea = async () => {
    try {
      const { zona_id, numero, total_columnas } = newLinea;

      if (!zona_id || !numero) {
        alert('Por favor, selecciona una zona y número de línea');
        return;
      }

      const zona = zonas.find(z => z.id === zona_id);
      const findEmptyPosition = (zona) => {
        if (!zona.lineas || zona.lineas.length === 0) return { x: 10, y: 10 };
        const columnWidth = 140;
        const rowHeight = 250;
        const padding = 20;
        const maxColumns = Math.max(3, Math.floor((zona.width - padding * 2) / columnWidth));

        for (let row = 0; row < 100; row++) {
          for (let col = 0; col < maxColumns; col++) {
            const testX = padding + (col * columnWidth);
            const testY = padding + (row * rowHeight);
            const hasCollision = zona.lineas.some(linea => {
              const lineaRight = (linea.x || 0) + (linea.width || 140);
              const lineaBottom = (linea.y || 0) + (linea.height || 200);
              const testRight = testX + columnWidth - padding;
              const testBottom = testY + rowHeight - padding;
              return !(testX > lineaRight || testRight < (linea.x || 0) ||
                       testY > lineaBottom || testBottom < (linea.y || 0));
            });
            if (!hasCollision) {
              return { x: testX, y: testY };
            }
          }
        }
        return { x: 10, y: zona.lineas.length * 260 };
      };

      const position = findEmptyPosition(zona);

      // Auto-expand zone if the new linea would be outside bounds
      const lineaEstimatedWidth = 140;
      const lineaEstimatedHeight = 250;
      const neededWidth = position.x + lineaEstimatedWidth + 20;
      const neededHeight = position.y + lineaEstimatedHeight + 60;
      let expandedZone = false;
      const zoneUpdates = {};
      if (neededWidth > zona.width) {
        zoneUpdates.width = neededWidth;
        expandedZone = true;
      }
      if (neededHeight > zona.height) {
        zoneUpdates.height = neededHeight;
        expandedZone = true;
      }
      if (expandedZone) {
        await authFetch(`${API_URL}/warehouse/zonas/${zona_id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(zoneUpdates)
        });
      }

      const response = await authFetch(`${API_URL}/warehouse/zonas/${zona_id}/lineas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ numero, x: position.x, y: position.y })
      });
      if (!response) return;

      const result = await response.json();
      if (result.success) {
        const {
          columna_width,
          columna_height,
          columna_spacing,
          total_niveles,
          nivel_picking_inicio,
          nivel_picking_fin
        } = DEFAULT_COLUMNA_CONFIG;

        const headerHeight = 30;
        const promises = [];

        for (let i = 1; i <= total_columnas; i++) {
          // Single vertical column: all columnas stacked top to bottom
          const x = columna_spacing;
          const y = headerHeight + columna_spacing + ((i - 1) * (columna_height + columna_spacing));

          promises.push(
            authFetch(`${API_URL}/warehouse/lineas/${result.data.id}/columnas`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                numero: i,
                x,
                y,
                total_niveles,
                nivel_picking_inicio,
                nivel_picking_fin
              })
            }).then(res => res.json()).then(async (res) => {
              if (res.success) {
                await authFetch(`${API_URL}/warehouse/columnas/${res.data.columna.id}`, {
                  method: 'PUT',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({
                    x,
                    y,
                    width: columna_width,
                    height: columna_height
                  })
                });
              }
            })
          );
        }

        await Promise.all(promises);
        fetchAlmacenData(almacenSeleccionado);
        setShowCreateLineaModal(false);
        setNewLinea({
          zona_id: null,
          numero: 1,
          total_columnas: 12
        });
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al crear la línea');
    }
  };

  const refrescarVista = () => {
    if (viewMode === 'visual') {
      fetchAlmacenData(almacenSeleccionado);
    } else {
      fetchEstructuraCompleta(almacenSeleccionado);
    }
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: '#fafafa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <header style={{
        background: 'white',
        borderBottom: `1px solid ${COLORS.neutralBorder}`,
        padding: '16px 24px',
        flexShrink: 0,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          maxWidth: '2000px',
          margin: '0 auto'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Warehouse size={24} color={COLORS.primary} />
            <div>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                Gestión de Almacén
              </h2>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: COLORS.neutralText }}>
                Control de inventario con alertas de vencimiento
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {/* Botón de Configuración */}
            <button
              onClick={() => setShowSettingsModal(true)}
              style={{
                padding: '8px',
                background: 'white',
                border: `1px solid ${COLORS.neutralBorder}`,
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = COLORS.neutral}
              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              title="Configuración"
            >
              <Settings size={18} color={COLORS.neutralText} />
            </button>

            <select
              value={almacenSeleccionado}
              onChange={(e) => setAlmacenSeleccionado(e.target.value)}
              style={{
                padding: '8px 12px',
                border: `1px solid ${COLORS.neutralBorder}`,
                borderRadius: '6px',
                fontSize: '13px',
                minWidth: '180px',
                cursor: 'pointer',
                background: 'white',
                color: '#1e293b',
                fontWeight: '500'
              }}
            >
              {almacenes.length === 0 && (
                <option value="">Sin almacenes</option>
              )}
              {almacenes.map(alm => (
                <option key={alm.id} value={alm.codigo}>
                  {alm.codigo} - {alm.nombre || 'Almacén'}
                </option>
              ))}
            </select>

            {/* Botón Exportar */}
            <button
              onClick={() => setShowExportModal(true)}
              disabled={!almacenSeleccionado}
              style={{
                padding: '8px 14px',
                background: almacenSeleccionado ? COLORS.secondary : COLORS.neutralBorder,
                color: almacenSeleccionado ? 'white' : '#94a3b8',
                border: 'none',
                borderRadius: '6px',
                cursor: almacenSeleccionado ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '13px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                boxShadow: almacenSeleccionado ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
              }}
              onMouseEnter={(e) => {
                if (almacenSeleccionado) {
                  e.currentTarget.style.background = COLORS.secondaryHover;
                }
              }}
              onMouseLeave={(e) => {
                if (almacenSeleccionado) {
                  e.currentTarget.style.background = COLORS.secondary;
                }
              }}
            >
              <Download size={16} />
              Exportar
            </button>

            <button
                  onClick={() => setShowCreateZoneModal(true)}
                  disabled={!almacenSeleccionado || creatingZone}
                  style={{
                    padding: '8px 16px',
                    background: almacenSeleccionado && !creatingZone ? COLORS.primary : COLORS.neutralBorder,
                    color: almacenSeleccionado && !creatingZone ? 'white' : '#94a3b8',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: almacenSeleccionado && !creatingZone ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '13px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    boxShadow: almacenSeleccionado ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (almacenSeleccionado && !creatingZone) {
                      e.currentTarget.style.background = COLORS.primaryHover;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (almacenSeleccionado && !creatingZone) {
                      e.currentTarget.style.background = COLORS.primary;
                    }
                  }}
                >
                  {creatingZone ? (
                    <>
                      <div style={{
                        width: '14px',
                        height: '14px',
                        border: '2px solid white',
                        borderTopColor: 'transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      Creando...
                    </>
                  ) : (
                    <>
                      <Plus size={16} />
                      Nueva Zona
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowCreateWarehouseModal(true)}
                  style={{
                    padding: '8px 14px',
                    background: COLORS.primary,
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '13px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = COLORS.accentHover}
                  onMouseLeave={(e) => e.currentTarget.style.background = COLORS.accent}
                  title="Crear nuevo almacén"
                >
                  <Building2 size={16} />
                  Nuevo Almacén
                </button>
          </div>
        </div>
      </header>

      {/* Pestañas de Vista */}
      <div style={{
        background: 'white',
        borderBottom: `1px solid ${COLORS.neutralBorder}`,
        padding: '0 24px',
        flexShrink: 0
      }}>
        <div style={{
          maxWidth: '2000px',
          margin: '0 auto',
          display: 'flex',
          gap: '4px'
        }}>
          <button
            onClick={() => handleViewModeChange('visual')}
            disabled={!almacenSeleccionado}
            style={{
              padding: '12px 20px',
              background: viewMode === 'visual' ? 'white' : 'transparent',
              color: viewMode === 'visual' ? COLORS.primary : COLORS.neutralText,
              border: 'none',
              borderBottom: viewMode === 'visual' ? `2px solid ${COLORS.primary}` : '2px solid transparent',
              cursor: almacenSeleccionado ? 'pointer' : 'not-allowed',
              fontSize: '13px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginBottom: '-1px',
              opacity: almacenSeleccionado ? 1 : 0.5,
              transition: 'all 0.2s ease'
            }}
          >
            <Eye size={16} />
            Vista Visual
          </button>
          <button
            onClick={() => handleViewModeChange('list')}
            disabled={!almacenSeleccionado}
            style={{
              padding: '12px 20px',
              background: viewMode === 'list' ? 'white' : 'transparent',
              color: viewMode === 'list' ? COLORS.primary : COLORS.neutralText,
              border: 'none',
              borderBottom: viewMode === 'list' ? `2px solid ${COLORS.primary}` : '2px solid transparent',
              cursor: almacenSeleccionado ? 'pointer' : 'not-allowed',
              fontSize: '13px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginBottom: '-1px',
              opacity: almacenSeleccionado ? 1 : 0.5,
              transition: 'all 0.2s ease'
            }}
          >
            <List size={16} />
            Vista Lista
          </button>
        </div>
      </div>

      {/* Contenido Principal */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {!almacenSeleccionado ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            gap: '16px',
            padding: '40px'
          }}>
            <Building2 size={64} color="#cbd5e1" />
            <div style={{ textAlign: 'center' }}>
              <p style={{ 
                color: COLORS.neutralText, 
                fontSize: '16px', 
                fontWeight: '500',
                margin: '0 0 8px' 
              }}>
                No hay almacenes disponibles
              </p>
              <p style={{ 
                color: '#94a3b8', 
                fontSize: '14px',
                margin: 0 
              }}>
                Crea tu primer almacén para comenzar
              </p>
            </div>
            <button
              onClick={() => setShowCreateWarehouseModal(true)}
              style={{
                padding: '12px 24px',
                background: COLORS.accent,
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: '600',
                marginTop: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = COLORS.accentHover;
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = COLORS.accent;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              }}
            >
              <Building2 size={18} />
              Crear Almacén
            </button>
          </div>
        ) : loading ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            gap: '16px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: `3px solid ${COLORS.neutralBorder}`,
              borderTopColor: COLORS.primary,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <p style={{ color: COLORS.neutralText, fontSize: '14px' }}>Cargando...</p>
          </div>
        ) : (
          <>
            {viewMode === 'visual' ? (
              <WarehouseCanvas
                token={token}
                API_URL={API_URL}
                zonas={zonas}
                setZonas={setZonas}
                snapToGrid={snapToGrid}
                maxZIndex={maxZIndex}
                setMaxZIndex={setMaxZIndex}
                almacenSeleccionado={almacenSeleccionado}
                refrescarVista={refrescarVista}
                setShowCreateLineaModal={setShowCreateLineaModal}
                setNewLinea={setNewLinea}
                newLinea={newLinea}
              />
            ) : (
              <ListWarehouse
                token={token}
                API_URL={API_URL}
                estructuraCompleta={estructuraCompleta}
                filtros={filtros}
                setFiltros={setFiltros}
                refrescarVista={refrescarVista}
                almacenSeleccionado={almacenSeleccionado}
              />
            )}
          </>
        )}
      </div>

      {/* MODALES */}

      {/* MODAL: Configuración */}
      {showSettingsModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(2px)'
          }}
          onClick={() => setShowSettingsModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '450px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '20px 24px',
              borderBottom: `1px solid ${COLORS.neutralBorder}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                color: '#1e293b'
              }}>
                <Settings size={20} />
                Configuración
              </h3>
              <button
                onClick={() => setShowSettingsModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: COLORS.neutralText,
                  borderRadius: '4px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = COLORS.neutral}
                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: '24px' }}>
              <div style={{
                marginBottom: '20px'
              }}>
                <label style={{
                  display: 'block',
                  marginBottom: '12px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#334155'
                }}>
                  Vista por defecto
                </label>
                
                <div style={{
                  display: 'flex',
                  gap: '12px'
                }}>
                  <button
                    onClick={() => {
                      handleViewModeChange('visual');
                      setShowSettingsModal(false);
                    }}
                    style={{
                      flex: 1,
                      padding: '14px',
                      background: viewMode === 'visual' ? COLORS.primary : 'white',
                      color: viewMode === 'visual' ? 'white' : COLORS.neutralText,
                      border: `2px solid ${viewMode === 'visual' ? COLORS.primary : COLORS.neutralBorder}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <Eye size={24} />
                    Vista Visual
                  </button>

                  <button
                    onClick={() => {
                      handleViewModeChange('list');
                      setShowSettingsModal(false);
                    }}
                    style={{
                      flex: 1,
                      padding: '14px',
                      background: viewMode === 'list' ? COLORS.primary : 'white',
                      color: viewMode === 'list' ? 'white' : COLORS.neutralText,
                      border: `2px solid ${viewMode === 'list' ? COLORS.primary : COLORS.neutralBorder}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <List size={24} />
                    Vista Lista
                  </button>
                </div>

                <p style={{
                  margin: '12px 0 0',
                  fontSize: '12px',
                  color: COLORS.neutralText
                }}>
                  Esta vista se cargará automáticamente al abrir el sistema
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ... resto de modales (mantengo los existentes sin cambios) ... */}
      
      {/* MODAL: Nuevo Almacén */}
      {showCreateWarehouseModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(2px)'
          }}
          onClick={() => setShowCreateWarehouseModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '480px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '20px 24px',
              borderBottom: `1px solid ${COLORS.neutralBorder}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ 
                margin: 0, 
                fontSize: '18px', 
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                color: '#1e293b'
              }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.accentHover})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Building2 size={20} color="white" />
                </div>
                Crear Nuevo Almacén
              </h3>
              <button
                onClick={() => setShowCreateWarehouseModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: COLORS.neutralText,
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = COLORS.neutral}
                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: '18px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#334155'
                }}>
                  Código del Almacén <span style={{ color: COLORS.danger }}>*</span>
                </label>
                <input
                  type="text"
                  value={newWarehouse.codigo}
                  onChange={(e) => setNewWarehouse({ 
                    ...newWarehouse, 
                    codigo: e.target.value.toUpperCase() 
                  })}
                  placeholder="ALM01"
                  maxLength={10}
                  style={{
                    width: 'calc(100% - 24px)',
                    padding: '10px 12px',
                    border: `2px solid ${COLORS.neutralBorder}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'monospace',
                    fontWeight: '600',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = COLORS.primary}
                  onBlur={(e) => e.currentTarget.style.borderColor = COLORS.neutralBorder}
                />
                <p style={{ 
                  margin: '6px 0 0', 
                  fontSize: '12px', 
                  color: COLORS.neutralText 
                }}>
                  Identificador único (ej: ALM01, WH-A, CENTRAL)
                </p>
              </div>

              <div style={{ marginBottom: '18px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#334155'
                }}>
                  Nombre <span style={{ color: COLORS.danger }}>*</span>
                </label>
                <input
                  type="text"
                  value={newWarehouse.nombre}
                  onChange={(e) => setNewWarehouse({ 
                    ...newWarehouse, 
                    nombre: e.target.value 
                  })}
                  placeholder="Almacén Central"
                  style={{
                    width: 'calc(100% - 24px)',
                    padding: '10px 12px',
                    border: `2px solid ${COLORS.neutralBorder}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = COLORS.primary}
                  onBlur={(e) => e.currentTarget.style.borderColor = COLORS.neutralBorder}
                />
              </div>

              <div style={{ marginBottom: '8px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#334155'
                }}>
                  Descripción
                </label>
                <textarea
                  value={newWarehouse.descripcion}
                  onChange={(e) => setNewWarehouse({ 
                    ...newWarehouse, 
                    descripcion: e.target.value 
                  })}
                  placeholder="Descripción opcional del almacén"
                  rows={3}
                  style={{
                    width: 'calc(100% - 24px)',
                    padding: '10px 12px',
                    border: `2px solid ${COLORS.neutralBorder}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = COLORS.primary}
                  onBlur={(e) => e.currentTarget.style.borderColor = COLORS.neutralBorder}
                />
              </div>
            </div>

            <div style={{
              padding: '16px 24px',
              borderTop: `1px solid ${COLORS.neutralBorder}`,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
              background: COLORS.neutral
            }}>
              <button
                onClick={() => setShowCreateWarehouseModal(false)}
                style={{
                  padding: '10px 18px',
                  background: 'white',
                  border: `1px solid ${COLORS.neutralBorder}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: COLORS.neutralText,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = COLORS.neutral}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              >
                Cancelar
              </button>
              <button
                onClick={createWarehouse}
                disabled={!newWarehouse.codigo || !newWarehouse.nombre}
                style={{
                  padding: '10px 18px',
                  background: (!newWarehouse.codigo || !newWarehouse.nombre) 
                    ? '#cbd5e1' 
                    : COLORS.accent,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (!newWarehouse.codigo || !newWarehouse.nombre) 
                    ? 'not-allowed' 
                    : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  boxShadow: (!newWarehouse.codigo || !newWarehouse.nombre) 
                    ? 'none' 
                    : '0 2px 4px rgba(0,0,0,0.1)'
                }}
                onMouseEnter={(e) => {
                  if (newWarehouse.codigo && newWarehouse.nombre) {
                    e.currentTarget.style.background = COLORS.accentHover;
                  }
                }}
                onMouseLeave={(e) => {
                  if (newWarehouse.codigo && newWarehouse.nombre) {
                    e.currentTarget.style.background = COLORS.accent;
                  }
                }}
              >
                <Save size={16} />
                Crear Almacén
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Exportar */}
      {showExportModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(2px)'
          }}
          onClick={() => setShowExportModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '450px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '20px 24px',
              borderBottom: `1px solid ${COLORS.neutralBorder}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                color: '#1e293b'
              }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  background: `linear-gradient(135deg, ${COLORS.secondary}, ${COLORS.secondaryHover})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Download size={18} color="white" />
                </div>
                Exportar Informe
              </h3>
              <button
                onClick={() => setShowExportModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: COLORS.neutralText,
                  borderRadius: '4px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = COLORS.neutral}
                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: '24px' }}>
              <div style={{
                padding: '12px 16px',
                background: COLORS.neutral,
                borderRadius: '8px',
                marginBottom: '20px',
                border: `1px solid ${COLORS.neutralBorder}`
              }}>
                <p style={{ 
                  margin: 0, 
                  fontSize: '13px', 
                  color: COLORS.neutralText 
                }}>
                  Almacén: <strong style={{ color: '#1e293b' }}>{almacenSeleccionado}</strong>
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {[
                  { format: 'csv', icon: FileText, label: 'CSV', color: COLORS.success },
                  { format: 'excel', icon: FileSpreadsheet, label: 'Excel', color: COLORS.success },
                  { format: 'pdf', icon: FileText, label: 'PDF', color: COLORS.danger }
                ].map(({ format, icon: Icon, label, color }) => (
                  <button
                    key={format}
                    onClick={() => exportReport(format)}
                    disabled={exportLoading}
                    style={{
                      padding: '14px 16px',
                      background: 'white',
                      border: `2px solid ${COLORS.neutralBorder}`,
                      borderRadius: '8px',
                      cursor: exportLoading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      fontSize: '14px',
                      fontWeight: '500',
                      opacity: exportLoading ? 0.6 : 1,
                      transition: 'all 0.2s ease',
                      color: '#1e293b'
                    }}
                    onMouseEnter={(e) => {
                      if (!exportLoading) {
                        e.currentTarget.style.borderColor = color;
                        e.currentTarget.style.background = COLORS.neutral;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!exportLoading) {
                        e.currentTarget.style.borderColor = COLORS.neutralBorder;
                        e.currentTarget.style.background = 'white';
                      }
                    }}
                  >
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '6px',
                      background: `${color}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Icon size={18} color={color} />
                    </div>
                    Exportar como {label}
                  </button>
                ))}
              </div>

              {exportLoading && (
                <div style={{
                  marginTop: '16px',
                  padding: '12px 16px',
                  background: '#eff6ff',
                  border: `1px solid ${COLORS.primary}30`,
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: COLORS.primary,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: `2px solid ${COLORS.primary}30`,
                    borderTopColor: COLORS.primary,
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Generando informe...
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Nueva Zona */}
      {showCreateZoneModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(2px)'
          }}
          onClick={() => setShowCreateZoneModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '480px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '20px 24px',
              borderBottom: `1px solid ${COLORS.neutralBorder}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ 
                margin: 0, 
                fontSize: '18px', 
                fontWeight: '600',
                color: '#1e293b'
              }}>
                Nueva Zona
              </h3>
              <button
                onClick={() => setShowCreateZoneModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: COLORS.neutralText,
                  borderRadius: '4px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = COLORS.neutral}
                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: '24px' }}>
              {/* Info: Feedback visual */}
              <div style={{
                padding: '12px 16px',
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '6px',
                marginBottom: '20px',
                fontSize: '13px',
                color: '#1e40af'
              }}>
                ℹ️ La nueva zona se creará al <strong>frente</strong> de todas las existentes
              </div>

              <div style={{ marginBottom: '18px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#334155'
                }}>
                  Código <span style={{ color: COLORS.danger }}>*</span>
                </label>
                <input
                  type="text"
                  value={newZone.codigo}
                  onChange={(e) => setNewZone({ ...newZone, codigo: e.target.value })}
                  placeholder="Z01"
                  style={{
                    width: 'calc(100% - 24px)',
                    padding: '10px 12px',
                    border: `2px solid ${COLORS.neutralBorder}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = COLORS.primary}
                  onBlur={(e) => e.currentTarget.style.borderColor = COLORS.neutralBorder}
                />
              </div>

              <div style={{ marginBottom: '18px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#334155'
                }}>
                  Nombre <span style={{ color: COLORS.danger }}>*</span>
                </label>
                <input
                  type="text"
                  value={newZone.nombre}
                  onChange={(e) => setNewZone({ ...newZone, nombre: e.target.value })}
                  placeholder="Almacenaje"
                  style={{
                    width: 'calc(100% - 24px)',
                    padding: '10px 12px',
                    border: `2px solid ${COLORS.neutralBorder}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = COLORS.primary}
                  onBlur={(e) => e.currentTarget.style.borderColor = COLORS.neutralBorder}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#334155'
                }}>
                  Color
                </label>
                <input
                  type="color"
                  value={newZone.color}
                  onChange={(e) => setNewZone({ ...newZone, color: e.target.value })}
                  style={{
                    width: '100%',
                    height: '44px',
                    border: `2px solid ${COLORS.neutralBorder}`,
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                />
              </div>
            </div>

            <div style={{
              padding: '16px 24px',
              borderTop: `1px solid ${COLORS.neutralBorder}`,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
              background: COLORS.neutral
            }}>
              <button
                onClick={() => setShowCreateZoneModal(false)}
                style={{
                  padding: '10px 18px',
                  background: 'white',
                  border: `1px solid ${COLORS.neutralBorder}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: COLORS.neutralText,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = COLORS.neutral}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              >
                Cancelar
              </button>
              <button
                onClick={createZona}
                disabled={!newZone.codigo || !newZone.nombre}
                style={{
                  padding: '10px 18px',
                  background: (!newZone.codigo || !newZone.nombre) 
                    ? '#cbd5e1' 
                    : COLORS.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (!newZone.codigo || !newZone.nombre) 
                    ? 'not-allowed' 
                    : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (newZone.codigo && newZone.nombre) {
                    e.currentTarget.style.background = COLORS.primaryHover;
                  }
                }}
                onMouseLeave={(e) => {
                  if (newZone.codigo && newZone.nombre) {
                    e.currentTarget.style.background = COLORS.primary;
                  }
                }}
              >
                <Save size={16} />
                Crear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Nueva Línea */}
      {showCreateLineaModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(2px)'
          }}
          onClick={() => setShowCreateLineaModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '450px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '20px 24px',
              borderBottom: `1px solid ${COLORS.neutralBorder}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600',
                color: '#1e293b'
              }}>
                Nueva Línea
              </h3>
              <button
                onClick={() => setShowCreateLineaModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: COLORS.neutralText,
                  borderRadius: '4px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = COLORS.neutral}
                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: '18px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#334155'
                }}>
                  Número de Línea
                </label>
                <input
                  type="number"
                  value={newLinea.numero}
                  onChange={(e) => setNewLinea({
                    ...newLinea,
                    numero: parseInt(e.target.value) || 1
                  })}
                  min="1"
                  style={{
                    width: '140px',
                    padding: '10px 12px',
                    border: `2px solid ${COLORS.neutralBorder}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = COLORS.primary}
                  onBlur={(e) => e.currentTarget.style.borderColor = COLORS.neutralBorder}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#334155'
                }}>
                  Total de Columnas
                </label>
                <input
                  type="number"
                  value={newLinea.total_columnas}
                  onChange={(e) => setNewLinea({
                    ...newLinea,
                    total_columnas: parseInt(e.target.value) || 12
                  })}
                  min="2"
                  max="40"
                  style={{
                    width: '140px',
                    padding: '10px 12px',
                    border: `2px solid ${COLORS.neutralBorder}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = COLORS.primary}
                  onBlur={(e) => e.currentTarget.style.borderColor = COLORS.neutralBorder}
                />
                <p style={{
                  margin: '8px 0 0',
                  fontSize: '12px',
                  color: COLORS.neutralText
                }}>
                  Se distribuirán automáticamente en fila horizontal
                </p>
              </div>

              <div style={{
                padding: '16px',
                background: '#eff6ff',
                border: `1px solid ${COLORS.primary}30`,
                borderRadius: '8px'
              }}>
                <h4 style={{
                  margin: '0 0 10px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: COLORS.primary,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  Configuración Automática
                </h4>
                <ul style={{
                  margin: 0,
                  paddingLeft: '20px',
                  fontSize: '12px',
                  color: '#1e40af',
                  lineHeight: '1.8'
                }}>
                  <li>Tamaño de columna: 45x45 px</li>
                  <li>Espaciado: 3 px</li>
                  <li>7 niveles por columna (Picking: niveles 1-2)</li>
                  <li>Cada nivel tiene 2 posiciones: P01 (Der.) y P02 (Izq.)</li>
                </ul>
              </div>
            </div>

            <div style={{
              padding: '16px 24px',
              borderTop: `1px solid ${COLORS.neutralBorder}`,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
              background: COLORS.neutral
            }}>
              <button
                onClick={() => setShowCreateLineaModal(false)}
                style={{
                  padding: '10px 18px',
                  background: 'white',
                  border: `1px solid ${COLORS.neutralBorder}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: COLORS.neutralText,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = COLORS.neutral}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              >
                Cancelar
              </button>
              <button
                onClick={createLinea}
                style={{
                  padding: '10px 18px',
                  background: COLORS.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = COLORS.primaryHover}
                onMouseLeave={(e) => e.currentTarget.style.background = COLORS.primary}
              >
                <Save size={16} />
                Crear
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes slideOut {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default WarehouseManagement;